import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  collectionGroup,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  query,
  where,
} from "firebase/firestore";
import {
  getStudiesPath,
  getMonitorAssignmentsPath,
  isActiveScopePath,
} from "../services/firestorePaths";
import { getActiveTenantId, getActiveSiteId } from "../services/activeTenant";
import { listUsers } from "../services/membershipService";
import { useNavigate } from "react-router-dom";
import { logoutAndClearSite } from "../services/logout";
import {
  ArrowLeft,
  LogOut,
  Microscope,
  Users,
  CheckCircle2,
  X,
  AlertTriangle,
  ChevronDown,
  Loader2,
  Trash2,
} from "lucide-react";

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

export default function AdminMonitors() {
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loadingReq, setLoadingReq] = useState(true);

  const [studies, setStudies] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loadingAssign, setLoadingAssign] = useState(true);
  const [saving, setSaving] = useState(false);

  // Who can be assigned a study. This was five hardcoded
  // monitorN@lunginnovations.com addresses, so no other site could ever name
  // its own monitors. Driven from user_roles instead: anyone holding the
  // `monitor` site role at the ACTIVE site. Loaded in refreshAssignments.
  const [monitors, setMonitors] = useState([]);

  const toMillis = (ts) => {
    if (!ts) return 0;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // ---- Requests ----
  // The collectionGroup query is now CONSTRAINED to the active site. It used to
  // run unscoped and filter in JavaScript, which meant every site's requests
  // were downloaded to the browser before being hidden. The rule matching this
  // is read-only and judged per document on these same two fields, so a query
  // that did not filter on them would be rejected outright rather than
  // over-returning.
  const fetchRequests = async () => {
    setLoadingReq(true);
    try {
      const snapshot = await getDocs(
        query(
          collectionGroup(db, "requests"),
          where("tenantId", "==", getActiveTenantId()),
          where("siteId", "==", getActiveSiteId())
        )
      );
      const data = snapshot.docs
        .map((d) => {
          const raw = d.data();
          return { id: d.id, ...raw, _createdAtMs: toMillis(raw.createdAt), path: d.ref.path };
        })
        // Belt and braces: the query and the rule both scope by field, this
        // also rejects anything sitting outside the active path.
        .filter((r) => isActiveScopePath(r.path))
        .sort((a, b) => b._createdAtMs - a._createdAtMs);
      setRequests(data);
    } catch (e) {
      console.error("Failed to load monitor requests:", e);
      setRequests([]);
    } finally {
      setLoadingReq(false);
    }
  };

  const handleAction = async (req, action) => {
    const ref = doc(db, req.path);
    await updateDoc(ref, {
      status: action,
      reviewedAt: new Date().toISOString(),
      vaultXLink: action === "Approved" ? "/ebinder?study=" + req.studyId : "",
    });
    setRequests((prev) =>
      prev.map((r) => (r.id === req.id ? { ...r, status: action } : r))
    );
  };

  const handleDelete = async (req) => {
    if (!window.confirm("Delete this request permanently?")) return;
    const ref = doc(db, req.path);
    await deleteDoc(ref);
    setRequests((prev) => prev.filter((r) => r.id !== req.id));
  };

  // ---- Assignments ----
  const loadStudies = async () => {
    const snap = await getDocs(collection(db, getStudiesPath()));
    return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  };

  const loadAssignments = async () => {
    const snap = await getDocs(collection(db, getMonitorAssignmentsPath()));
    return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  };

  // Everyone holding the `monitor` site role at the ACTIVE site. listUsers()
  // returns normalized membership, so this reads the same shape /manage writes.
  const loadMonitors = async () => {
    const tenantId = getActiveTenantId();
    const siteId = getActiveSiteId();
    try {
      const users = await listUsers();
      return users
        .filter((u) => u.memberships?.[tenantId]?.sites?.[siteId] === "monitor")
        .map((u) => u.email)
        .sort();
    } catch (e) {
      console.error("Failed to load monitors:", e);
      return [];
    }
  };

  const refreshAssignments = async () => {
    setLoadingAssign(true);
    try {
      const [s, a, m] = await Promise.all([
        loadStudies(),
        loadAssignments(),
        loadMonitors(),
      ]);
      setStudies(s);
      setAssignments(a);
      setMonitors(m);
    } finally {
      setLoadingAssign(false);
    }
  };

  const assignStudy = async (monitorEmail, study) => {
    try {
      setSaving(true);
      const existing = assignments.find((a) => a.monitorEmail === monitorEmail);
      if (existing) await deleteDoc(doc(db, getMonitorAssignmentsPath(), existing.id));
      const ref = doc(collection(db, getMonitorAssignmentsPath()));
      await setDoc(ref, {
        monitorEmail,
        studyId: study.id,
        studyName: study.NAME || "",
        studyCode: study.STUDY_ID || "",
        active: true,
        assignedAt: new Date().toISOString(),
      });
      await refreshAssignments();
    } finally {
      setSaving(false);
    }
  };

  const removeAssignment = async (assignmentId) => {
    setSaving(true);
    await deleteDoc(doc(db, getMonitorAssignmentsPath(), assignmentId));
    await refreshAssignments();
    setSaving(false);
  };

  const getMonitorAssignment = (email) =>
    assignments.find((a) => a.monitorEmail === email);

  useEffect(() => {
    fetchRequests();
    refreshAssignments();
  }, []);

  const handleLogout = async () => {
    await logoutAndClearSite(); // also clears the cached userRole
    navigate("/login");
  };

  const statusChip = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved")
      return "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border bg-emerald-400/15 text-emerald-300 border-emerald-400/30";
    if (s === "denied")
      return "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border bg-rose-400/15 text-rose-300 border-rose-400/30";
    return "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border bg-amber-400/15 text-amber-300 border-amber-400/30";
  };

  const statusDot = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved") return "w-1.5 h-1.5 rounded-full bg-emerald-400";
    if (s === "denied") return "w-1.5 h-1.5 rounded-full bg-rose-400";
    return "w-1.5 h-1.5 rounded-full bg-amber-400";
  };

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      {/* Sticky header */}
      <header className="sticky top-0 z-30 backdrop-blur-2xl bg-slate-950/55 border-b border-white/10 shadow-[0_8px_30px_-12px_rgba(124,58,237,0.35)]">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Back</span>
          </button>

          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
              <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                <Microscope className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold leading-none">
                Lung Innovations · RegX
              </div>
              <h1 className="text-sm sm:text-base font-semibold tracking-tight text-white leading-tight truncate">
                Monitor Management
              </h1>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="hidden sm:flex w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-red-500/15 hover:border-red-400/30 transition-all items-center justify-center group shrink-0"
            title="Log out"
          >
            <LogOut className="w-4 h-4 text-slate-200 group-hover:text-red-300 transition" />
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 space-y-4">

        {/* Section 1: Monitor Requests */}
        <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-70 pointer-events-none" />

          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-300" />
            <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">
              Monitor Requests
            </h2>
            {!loadingReq && (
              <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-white/[0.06] border border-white/10 text-slate-300">
                {requests.length}
              </span>
            )}
          </div>

          {loadingReq ? (
            <div className="flex items-center justify-center gap-2 py-12 text-xs uppercase tracking-[0.25em] text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin text-purple-300" />
              Loading requests…
            </div>
          ) : requests.length === 0 ? (
            <div className="py-10 text-center text-xs text-slate-500 italic">No requests yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5">
                    {["Created", "Monitor", "Study", "Sections", "Status", "Actions"].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-400">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {requests.map((req) => (
                    <tr key={req.id} className="hover:bg-white/[0.02] transition align-top">
                      <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">
                        {req._createdAtMs ? new Date(req._createdAtMs).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-200 whitespace-nowrap">
                        {req.monitorName || "Unknown"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-200 whitespace-nowrap">
                        {req.studyName}{" "}
                        <span className="text-slate-500 font-mono">({req.studyId})</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-300 max-w-[200px]">
                        {Array.isArray(req.requestedSections)
                          ? req.requestedSections.join(", ")
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={statusChip(req.status)}>
                          <span className={statusDot(req.status)} />
                          {req.status || "Pending"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            onClick={() => handleAction(req, "Approved")}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-400/30 hover:bg-emerald-500/25 text-[11px] font-medium text-emerald-200 hover:text-white transition"
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleAction(req, "Denied")}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-rose-500/15 border border-rose-400/30 hover:bg-rose-500/25 text-[11px] font-medium text-rose-200 hover:text-white transition"
                          >
                            <X className="w-3 h-3" />
                            Deny
                          </button>
                          <button
                            onClick={() => handleDelete(req)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[11px] font-medium text-slate-300 hover:text-white transition"
                          >
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Section 2: Monitor Assignments */}
        <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 opacity-70 pointer-events-none" />

          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
            <Users className="w-3.5 h-3.5 text-blue-300" />
            <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">
              Monitor Assignments
            </h2>
            {saving && (
              <span className="ml-auto flex items-center gap-1 text-[10px] text-slate-400">
                <Loader2 className="w-3 h-3 animate-spin text-purple-300" />
                Saving…
              </span>
            )}
          </div>

          {loadingAssign ? (
            <div className="flex items-center justify-center gap-2 py-12 text-xs uppercase tracking-[0.25em] text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin text-purple-300" />
              Loading assignments…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5">
                    {["Monitor", "Current Study", "Actions"].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-400">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {monitors.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-xs text-slate-400">
                        No monitors at this site yet. Give someone the{" "}
                        <span className="font-mono text-slate-300">monitor</span> role for
                        this site under Manage → User access.
                      </td>
                    </tr>
                  )}
                  {monitors.map((email) => {
                    const assignment = getMonitorAssignment(email);
                    return (
                      <tr key={email} className="hover:bg-white/[0.02] transition">
                        <td className="px-4 py-3 text-xs font-mono text-slate-200">{email}</td>
                        <td className="px-4 py-3 text-xs">
                          {assignment ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border bg-purple-400/15 text-purple-300 border-purple-400/30">
                              <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                              {assignment.studyName || "Study"}
                              {assignment.studyCode && (
                                <span className="text-purple-400/70 font-mono">
                                  ({assignment.studyCode})
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-slate-500 italic text-xs">No assignment</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {assignment ? (
                            <button
                              onClick={() => removeAssignment(assignment.id)}
                              disabled={saving}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-rose-500/15 border border-rose-400/30 hover:bg-rose-500/25 text-[11px] font-medium text-rose-200 hover:text-white transition disabled:opacity-50"
                            >
                              <X className="w-3 h-3" />
                              Remove
                            </button>
                          ) : (
                            <div className="relative inline-block">
                              <select
                                onChange={(e) => {
                                  const study = studies.find((s) => s.id === e.target.value);
                                  if (study) assignStudy(email, study);
                                }}
                                defaultValue=""
                                disabled={saving}
                                className="appearance-none pl-3 pr-8 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-xs text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 transition cursor-pointer disabled:opacity-50"
                              >
                                <option value="" disabled className="bg-[#0b0820] text-slate-100">
                                  Assign study…
                                </option>
                                {studies.map((s) => (
                                  <option key={s.id} value={s.id} className="bg-[#0b0820] text-slate-100">
                                    {s.NAME || "Study"}{s.STUDY_ID ? ` (${s.STUDY_ID})` : ""}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="h-8" />
      </div>
    </div>
  );
}
